<template>
  <main class="registry">
    <div class="registry__header">
      <button-back />
      <h1>Реестр выданных справок</h1>
      <form class="registry__form" v-on:submit.prevent v-if="hasFetchedOptions">
        <date-range-with-presets
          v-bind:presets="presetsOfDates"
          v-bind:value="selectedDates"
          v-on:input="onInputDates"
        />
        <input-autocomplete
          clearable
          no-data-text="Результатов нет"
          title="Сотрудник"
          v-bind:options="employeeOptions"
          v-bind:value="selectedEmployeeId"
          v-on:input="onInputEmployee"
          class="registry__autocomplete"
        />
        <input-autocomplete
          clearable
          no-data-text="Результатов нет"
          title="Пациент"
          v-bind:options="patientOptions"
          v-bind:value="selectedPatientId"
          v-on:input="onInputPatient"
          class="registry__autocomplete"
        />
      </form>
    </div>
    <div class="registry__body">
      <span v-if="isEmptyResult">Результатов нет</span>
      <div
        class="registry__table"
        v-else-if="hasFetchedList"
      >
        <div class="registry__table__head">
          <div
            class="registry__table__cell"
            v-for="{ field, title, sorts } in columns"
            v-bind:key="field"
          >
            <input-sort
              class="registry__table__sort"
              v-bind:field="field"
              v-bind:sorts="sorts"
              v-bind:title="title"
              v-bind:value="selectedSort"
              v-if="sorts.length"
              v-on:input="onInputSort"
            />
          </div>
        </div>
        <div class="registry__table__body">
          <component
            v-bind:is="component"
            class="registry__table__row"
            v-for="{
              id, actions, cells, component, documentName, rowKey, navigation,
            } in mappedRows"
            v-bind:key="rowKey"
            v-bind:to="navigation"
          >
            <div
              class="registry__table__cell"
              v-for="{ value, cellKey } in cells"
              v-bind:key="cellKey"
            >
              {{ value }}
            </div>
            <button
              v-if="actions.includes('print')"
              class="registry__table__cell registry__table__action registry__table__print"
              type="button"
              title="Распечатать справку"
              v-on:click.prevent.stop="onPrint(id, documentName)"
              v-bind:disabled="isAwaitDownload"
            />
            <button
              v-if="actions.includes('download')"
              class="registry__table__cell registry__table__action registry__table__download"
              type="button"
              title="Скачать справку"
              v-on:click.prevent.stop="onDownload(id, documentName)"
              v-bind:disabled="isAwaitDownload"
            />
            <button
              v-if="actions.includes('delete')"
              class="registry__table__cell registry__table__action registry__table__delete"
              type="button"
              title="Удалить справку"
              v-on:click.prevent.stop="onDelete(id)"
              v-bind:disabled="isAwaitDelete"
            />
          </component>
        </div>
      </div>
    </div>
    <iframe class="registry__print" ref="pdfView" title="pdfView" />
  </main>
</template>

<script>
import ButtonBack from '@/components/ButtonBack.vue';
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import InputAutocomplete from '@/components/InputAutocomplete.vue';
import dateMixin from '@/mixins/dateMixin';
import debounce from 'lodash.debounce';
import notifierMixin from '@/mixins/notifierMixin';
import saveFileMixin from '@/mixins/saveFileMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';

/**
 * @description Компонент поля сортировки.
 */
const InputSort = {
  computed: {
    headTitle() {
      const head = this.sortedItems[0];
      return head ? head.title : '';
    },
    isNotSelected() {
      return this.sorts.some(({ value }) => value === this.value) !== true;
    },
    sortedItems() {
      const items = [
        {
          checked: this.isNotSelected,
          title: 'Без сортировки',
          value: '',
        },
        ...this.sorts.map(({ title, value }) => ({
          checked: value === this.value,
          title,
          value,
        })),
      ];
      let slicedPosition = 0;
      const checkedIndex = items.findIndex(({ checked }) => checked);
      if (checkedIndex !== -1) {
        slicedPosition = checkedIndex + 1;
      }
      if (slicedPosition === items.length) {
        return items;
      }
      return [
        ...items.slice(slicedPosition),
        ...items.slice(0, slicedPosition),
      ];
    },
  },
  props: {
    field: { required: true, type: String },
    sorts: { required: true, type: Array },
    title: { required: true, type: String },
    value: { required: true, type: String },
  },
  render(createElement) {
    const name = `report-sort-${this.field}`;
    return createElement('label', { attrs: { title: this.headTitle } }, [
      createElement('span', this.title).children[0],
      ...this.sortedItems.map(({ checked, title, value }) => createElement('input', {
        attrs: {
          'aria-label': title,
          checked,
          name,
          title,
          type: 'radio',
        },
        domProps: { checked },
        key: `column:${this.field}:sort:${value}`,
        on: { input: () => { this.$emit('input', value); } },
      })),
    ]);
  },
};

/**
 * @description Компонент страницы реестра выданных справок.
 */
export default {
  components: {
    ButtonBack,
    DateRangeWithPresets,
    InputAutocomplete,
    InputSort,
  },
  computed: {
    isEmptyResult() {
      return this.hasFetchedList && this.rows.length === 0;
    },
    presetsOfDates() {
      return this.periodTypes.map(({ periodDates, title, value }) => ({
        id: value,
        label: title,
        title,
        toValue: () => periodDates,
      }));
    },
    mappedRows() {
      return this.rows.map(({
        id, actions, cells, document_name: documentName,
      }) => {
        const rowKey = `rows:${id}`;
        const mappedCells = cells.map((value, cellIndex) => ({
          value,
          cellKey: `${rowKey}:cells:${cellIndex}`,
        }));
        const component = actions.includes('view') ? 'router-link' : 'div';
        return {
          id,
          navigation: { name: 'ClinicPanelNdflCertificate', params: { id } },
          actions,
          cells: mappedCells,
          component,
          documentName,
          rowKey,
        };
      });
    },
  },
  created() {
    this
      .fetchOptions()
      .then(() => {
        if (this.issuedDate) {
          const date = new Date(this.issuedDate);
          this.selectedDates = [date, date];
        } else {
          this
            .periodTypes
            .slice(2, 3)
            .forEach(({ periodDates }) => {
              this.selectedDates = periodDates;
            });
        }
        this.reloadList();
      })
      .finally(() => { this.hasFetchedOptions = true; });
  },
  data() {
    return {
      columns: [],
      employeeOptions: [],
      hasFetchedList: false,
      /**
       * @description Опции получены.
       * @type {boolean}
       */
      hasFetchedOptions: false,
      isAwaitRequest: false,
      patientOptions: [],
      periodTypes: [],
      rows: [],
      selectedDates: [],
      selectedEmployeeId: null,
      selectedPatientId: this.initialPatientId,
      selectedSort: 'by-date-asc',
      isAwaitDownload: false,
      isAwaitDelete: false,
    };
  },
  methods: {
    /**
     * @description Получить опции.
     * @returns {Promise}
     */
    fetchOptions() {
      return this
        .$registryNdflCertificateGateway
        .viewOptions()
        .then((result) => {
          result.forEach(({ employees, patients, periods }) => {
            this.employeeOptions = employees;
            this.patientOptions = patients;
            this.periodTypes = periods
              .map(({ from_to: [from, to], title, value }) => ({
                periodDates: [new Date(from), new Date(to)],
                title,
                value,
              }));
          });
        });
    },
    debounceReloadList: debounce(function _() { this.reloadList(); }, 300),
    /**
     * @description Перезагрузить список.
     * @returns {void}
     */
    reloadList() {
      this.isAwaitRequest = true;
      const onFail = (error) => {
        this.notifyFail(error);
      };
      const onSuccess = ({ columns, rows }) => {
        this.columns = columns;
        this.rows = rows;
      };
      const filters = {
        employeeId: this.selectedEmployeeId,
        patientId: this.selectedPatientId,
        period: this
          .selectedDates
          .map((date) => this.formatDateToISO(date))
          .join(':'),
        sort: this.selectedSort,
      };
      this
        .$registryNdflCertificateGateway
        .viewList(filters)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => {
          this.hasFetchedList = true;
          this.isAwaitRequest = false;
        });
    },
    /**
     * @description Обработать выбор периода для расчета.
     * @returns {void}
     */
    onInputDates(value) {
      if (value.length === 2) {
        this.selectedDates = value.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
        this.debounceReloadList();
      } else {
        this.selectedDates = value;
      }
    },
    /**
     * @description Обработать выбор сотрудника.
     * @param {number} employeeId
     * @returns {void}
     */
    onInputEmployee(employeeId) {
      this.selectedEmployeeId = employeeId;
      this.debounceReloadList();
    },
    /**
     * @description Обработать выбор пациента.
     * @param {number} patientId
     * @returns {void}
     */
    onInputPatient(patientId) {
      this.selectedPatientId = patientId;
      this.debounceReloadList();
    },
    /**
     * @description Обработать выбор сортировки.
     * @param {string} value
     * @returns {void}
     */
    onInputSort(value) {
      this.selectedSort = value;
      this.debounceReloadList();
    },
    /**
     * @description Обработать удаление справки.
     * @param {number} certificateId
     * @returns {void}
     */
    async onDelete(certificateId) {
      const dialog = await this.showRemoveDialog('Вы действительно хотите удалить справку?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      this.isAwaitDelete = true;
      const onFail = (error) => { this.notifyFail(error.title); };
      const onSuccess = (data) => {
        this.notifySuccess(data.message);
        this.reloadList();
      };
      this
        .$registryNdflCertificateGateway
        .deleteById(certificateId)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitDelete = false; });
    },
    /**
     * @description Обработать скачивание справки.
     * @param {number} certificateId Идентификатор справки.
     * @param {string} name          Название файла.
     * @returns {void}
     */
    onDownload(certificateId, name) {
      this.isAwaitDownload = true;
      const onFail = (error) => { this.notifyFail(error.title); };
      const onSuccess = (blob) => {
        this.saveFile(blob, name);
      };
      this
        .$registryNdflCertificateGateway
        .downloadById(certificateId)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitDownload = false; });
    },
    onPrint(certificateId) {
      this.isAwaitDownload = true;
      const onFail = (error) => { this.notifyFail(error.title); };
      const onSuccess = (response) => {
        const container = this.$refs.pdfView;
        container.setAttribute('src', URL.createObjectURL(response));
        container.onload = () => {
          container.contentWindow.print();
        };
      };
      this
        .$registryNdflCertificateGateway
        .printById(certificateId)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitDownload = false; });
    },
  },
  mixins: [
    dateMixin,
    notifierMixin,
    saveFileMixin,
    builtInDialogMixin,
  ],
  name: 'ClinicPanelRegistryNdflCertificateList',
  props: {
    /**
     * @description Дата выдачи справки.
     */
    issuedDate: { default: () => '', type: String },
    initialPatientId: { default: () => null, type: Number },
  },
};
</script>

<style lang="scss">

.registry {
  width: 100%;
  height: 100%;
  overflow-y: auto;

  &__header {
    background: #F2F5F8;
    width: 100%;
    padding: 38px 20px 48px 23px;
  }

  &__body {
    padding: 22px 20px 100px 23px;
  }

  &__form {
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;
    gap: 23px;
    margin: 30px 0 0 0;
  }
  &__autocomplete {
    .v-text-field__details {
      display: none;
    }
    .v-input__slot {
      margin-bottom: 0;
    }
  }
  &__table {
    overflow: auto;
    counter-reset: counterEmployees;
    &__head,
    &__row {
      text-decoration: none;
      color: black !important;
      width: 100%;
      display: grid;
      grid-template-columns: 120px 200px 250px 1fr 1fr 200px 220px repeat(3, 40px);
      grid-gap: 4px;
      align-items: center;
      min-width: 1500px;
      font-size: 14px;
      line-height: 17px;
      border-bottom: 1px solid #EAEFF3;
    }
    &__head {
      font-weight: 700;
      border-bottom: 2px solid #0489e0;
      padding: 10px 0;
    }
    &__sort {
      cursor: pointer;
      input {
        display: none;
      }
      &:after {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: 8px;
        margin: 0 0 0 5px;
        min-height: 8px;
        min-width: 8px;
        width: 8px;
      }
    }
    &__cell {
      padding: 15px 8px;
      text-align: left;
      text-transform: none;
    }
    &__action {
      width: 20px;
      height: 20px;
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      cursor: pointer;
    }
    &__view {
      background-image: url("@/assets/img/eye-open.svg");
      &:hover {
        background-image: url("@/assets/img/eye-open-blue.svg");
      }
    }
    &__print {
      grid-column: -4;
      background-image: url("@/assets/img/print-grey.svg");
      background-size: 20px 20px;
      &:hover {
        background-image: url("@/assets/img/print.svg");
      }
    }
    &__download {
      grid-column: -3;
      background-image: url("@/assets/img/download-file.svg");
      background-size: 20px 20px;
      &:hover {
        background-image: url("@/assets/img/download-file-blue.svg");
      }
    }
    &__delete {
      grid-column: -2;
      width: 12px;
      height: 12px;
      background-image: url("@/assets/img/exit-grey.svg");
      background-size: 12px 12px;
      &:hover {
        background-image: url("@/assets/img/exit-blue.svg");
      }
    }
  }
  &__print {
    display: none;
  }
}
</style>
